/**
 * Hook del detalle de una Locación (US-2.2). La red vive en `locationApi`; acá solo
 * el estado de la vista (loading / no encontrado / error / refresh), separable de la
 * presentación — mismo patrón que `useAlly`.
 *
 * Tras un `sync` fallido que devuelve 502 el error queda en `error`; tras uno exitoso
 * el server responde la locación actualizada y `refresh()` recarga el detalle.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AdminLocation } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { locationApi } from '../api/locationApi';

export interface UseLocationResult {
  location: AdminLocation | null;
  loading: boolean;
  error: string | null;
  /** `true` cuando el backend responde 404 (locación inexistente). */
  notFound: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar la ubicación.';
}

export function useLocation(locationId: string | undefined): UseLocationResult {
  const [location, setLocation] = useState<AdminLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!locationId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    locationApi
      .get(locationId)
      .then((result) => {
        if (!cancelled) setLocation(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else {
          setError(toErrorMessage(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [locationId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { location, loading, error, notFound, refresh };
}
